import React, { useEffect, useRef } from 'react';
import { Box } from '@heycater/design-system';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import { Sticky } from 'hub/components/Sticky';
import { cssVars } from 'lib/cssVariables';
import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import useDebounce from 'shared/hooks/useDebounce';
import { useCurrentSection } from 'static_pages/marketing_pages/dynamic_landing_page/components/PageSections';
import { buildFunnelUrl } from 'static_pages/marketing_pages/helpers';

export const TrackedSection = ({ id, label, children, ...props }) => {
  const { addSection, topOffset } = useCurrentSection();

  useEffect(() => {
    addSection({ id, label });
  }, []);

  return (
    <TrackedSectionBox
      scrollTopOffset={topOffset}
      id={id}
      as="section"
      {...props}
    >
      {children}
    </TrackedSectionBox>
  );
};

function StickySectionsNavigation({ title }) {
  const isMobile = useBreakpointDown('sm');
  const { sections, activeSection } = useCurrentSection();
  const { ref, inView } = useInView({ threshold: 1 });
  const { t } = useTranslation('common');
  const isSticky = !inView;
  const router = useRouter();

  const sectionElementsRef = useRef({});
  const parentRef = useRef(null);

  const debouncedActiveSection = useDebounce(activeSection, 300);

  useEffect(() => {
    if (!debouncedActiveSection) {
      return;
    }
    function horizontallyCenterTheActiveSectionLabel() {
      const activeSectionElement =
        sectionElementsRef.current[debouncedActiveSection.id];
      if (activeSectionElement && parentRef.current) {
        const parentElement = parentRef.current;
        const childElement = activeSectionElement;

        const parentWidth = parentElement.offsetWidth;
        const childWidth = childElement.offsetWidth;
        const childOffsetLeft = childElement.offsetLeft;

        const scrollLeft = childOffsetLeft - (parentWidth - childWidth) / 2;

        parentElement.scrollLeft = scrollLeft;
      }
    }

    horizontallyCenterTheActiveSectionLabel();
  }, [debouncedActiveSection]);

  return (
    <>
      {title && <BackgroundColor>{title}</BackgroundColor>}
      <Sticky
        hideOnScroll={isMobile}
        as="nav"
        top={`var(${cssVars.topBarHeight.name})`}
      >
        <Box position="relative">
          <StickinessTracker ref={ref} />
          <BackgroundColor isSticky={isSticky}>
            <SectionList ref={parentRef}>
              {sections.map(({ id, label }) => (
                <ListItem key={id}>
                  <SectionLink
                    ref={(element) =>
                      (sectionElementsRef.current[id] = element)
                    }
                    as="a"
                    href={`#${id}`}
                    active={activeSection?.id === id}
                  >
                    {label}
                  </SectionLink>
                </ListItem>
              ))}
              <ListItem>
                <Link passHref href={buildFunnelUrl({ router })}>
                  <CtaLink as="a" active={true}>
                    {t('labels.findCaterersNow')}
                  </CtaLink>
                </Link>
              </ListItem>
            </SectionList>
          </BackgroundColor>
        </Box>
      </Sticky>
    </>
  );
}

const StickinessTracker = styled.div`
  position: absolute;
  top: calc(-2px - var(${cssVars.topBarHeight.name}));
  left: 0;
  right: 0;
  pointer-events: none;
  margin-top: -1px;
  height: 1px;
`;

const BackgroundColor = styled.div`
  background-color: ${({ isSticky }) => (isSticky ? '#f8f8f8' : '#fff')};
  ${({ theme }) => theme.breakpoints.up('md')} {
    background-color: #fff;
  }
`;

const ListItem = styled.li`
  padding: 0;
  flex-shrink: 0;
  margin: 0;
`;

const SectionList = styled.ul`
  padding: ${({ theme }) => theme.spacing(2)}px;
  list-style: none;
  display: flex;
  margin: 0;
  overflow-x: auto;
  scroll-behavior: smooth;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    justify-content: flex-start;
  }
`;

const SectionLink = styled.a`
  display: block;
  margin-right: 1rem;
  color: ${({ active, theme }) =>
    active ? theme.palette.common.white : theme.palette.text.secondary};
  text-decoration: none;
  background: ${({ active, theme }) =>
    active ? theme.palette.secondary.light : theme.palette.text.disabled};
  border-radius: 500px;
  padding: ${({ theme }) => theme.spacing(0, 2.5)};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  height: 38px;
  display: grid;
  place-content: center;

  &:hover {
    background: ${({ active }) => !active && 'hsla(69, 53%, 70%, .19)'};
    color: ${({ theme }) => theme.palette.secondary.dark};
  }
`;

const CtaLink = styled.a`
  display: block;
  margin-right: 1rem;
  color: ${({ theme }) => theme.palette.common.white};
  text-decoration: none;
  background: ${({ theme }) => theme.palette.primary.main};
  border-radius: 500px;
  padding: ${({ theme }) => theme.spacing(1.5, 2.5)};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  height: 38px;
  display: grid;
  place-content: center;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.palette.primary.light};
  }
`;

const TrackedSectionBox = styled(Box)`
  scroll-margin-top: ${({ scrollTopOffset }) => scrollTopOffset}px;
`;

export default StickySectionsNavigation;
